import React, { useState, useEffect, useContext, useRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./EditReport.css";
import {
  getAllReports,
  updateReport,
  deleteReport,
  sendReport,
} from "../../../api/reportsApi";
import "../NewReport/NewReport.css";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import EditReportModal from "./EditReportModal";
import Select from "react-select";
import { getUserById } from "../../../api/api";
import { getUsersSummary } from "../../../api/api";

registerLocale("ru", ru);

// Добавляем компонент LoadingIndicator
const LoadingIndicator = () => (
  <div className="select-loading-indicator">
    <div className="select-loading-dot"></div>
    <div className="select-loading-dot"></div>
    <div className="select-loading-dot"></div>
  </div>
);

const checkUserAccess = (user, accessLevel) => {
  return user?.access_levels?.some((level) => level.name === accessLevel);
};

// Добавляем функцию фильтрации отчетов (после всех импортов)
const filterAndFormatReports = (
  allReports,
  currentMonth,
  currentYear,
  isWithinEditPeriod,
  userData,
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isAdmin = checkUserAccess(userData, "АДМС");
  const isSpecialUser = [176, 67].includes(userData?.id);
  const hasThreeMonthAccess = isAdmin || isSpecialUser;

  // Определяем нередактируемые типы работ
  const nonEditableWorkTypes = [
    "Отпуск",
    "Отпуск без содержания",
    "Больничный",
    "Командировка",
  ];

  // Фильтруем отчеты
  const filteredReports = allReports.filter((report) => {
    const reportDate = new Date(report.fact_date_start);
    reportDate.setHours(12, 0, 0, 0);
    const reportMonth = reportDate.getMonth();
    const reportYear = reportDate.getFullYear();

    if (hasThreeMonthAccess) {
      // Для АДМС показываем отчеты за текущий и два предыдущих месяца (без изменений)
      if (currentMonth === 0) {
        // Если текущий месяц - январь
        return (
          (reportMonth === 10 && reportYear === currentYear - 1) || // Ноябрь предыдущего года
          (reportMonth === 11 && reportYear === currentYear - 1) || // Декабрь предыдущего года
          (reportMonth === currentMonth && reportYear === currentYear) // Январь текущего года
        );
      } else if (currentMonth === 1) {
        // Если текущий месяц - февраль
        return (
          (reportMonth === 11 && reportYear === currentYear - 1) || // Декабрь предыдущего года
          (reportMonth === 0 && reportYear === currentYear) || // Январь текущего года
          (reportMonth === currentMonth && reportYear === currentYear) // Февраль текущего года
        );
      } else {
        return (
          (reportMonth === currentMonth - 2 && reportYear === currentYear) || // Два месяца назад
          (reportMonth === currentMonth - 1 && reportYear === currentYear) || // Предыдущий месяц
          (reportMonth === currentMonth && reportYear === currentYear) // Текущий месяц
        );
      }
    } else if (isWithinEditPeriod) {
      return (
        (reportMonth === currentMonth - 1 && reportYear === currentYear) ||
        (reportMonth === currentMonth && reportYear === currentYear)
      );
    } else {
      return reportMonth === currentMonth && reportYear === currentYear;
    }
  });

  // Добавляем флаг isEditable
  const reportsWithEditFlag = filteredReports.map((report) => {
    const reportDate = new Date(report.fact_date_start);
    const daysDiff = Math.floor((today - reportDate) / (1000 * 60 * 60 * 24));

    // Проверяем, согласован ли отчет если это переработка
    const isOvertimeApproved =
      report.is_overtime &&
      report.overtime_statuses &&
      Object.values(report.overtime_statuses).every(
        (status) => status === "Согласован",
      );

    // Проверяем, является ли тип работы нередактируемым
    const isNonEditableWorkType = nonEditableWorkTypes.includes(
      report.portal_job_type?.name,
    );

    // Отчеты с отгулами нельзя редактировать обычным пользователям, но можно администраторам
    const isDayOffEditable = !report.is_day_off || hasThreeMonthAccess;

    // Проверяем новое поле is_editable из API
    // false - не разрешать редактировать, null/true - разрешать
    const isApiEditable = report.is_editable !== false;

    // Администраторы могут редактировать отчеты за текущий и два предыдущих месяца.
    // Обычные пользователи — только отчёты за последние 4 дня (включая сегодня).
    // Также учитываем is_editable из API, согласованные переработки и типы работ
    const isEditable =
      isApiEditable &&
      (hasThreeMonthAccess || daysDiff <= 3) &&
      !isOvertimeApproved &&
      !isNonEditableWorkType &&
      isDayOffEditable;

    return {
      ...report,
      isEditable,
    };
  });

  // Сортируем отчеты
  return [...reportsWithEditFlag].sort(
    (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start),
  );
};

// Функция для форматирования названия проекта
const formatProjectName = (projectName) => {
  if (!projectName) return "-";

  // Разбиваем название по подчеркиваниям
  const parts = projectName.split("_");

  if (parts.length < 2) {
    // Если нет подчеркиваний, возвращаем как есть
    return projectName;
  }

  // Берем первую часть (код проекта)
  const projectCode = parts[0];

  // Ищем суффикс в последней части (К1, К2, Б1, Б2 и т.д.)
  const lastPart = parts[parts.length - 1];
  const suffixMatch = lastPart.match(/^([КБ]\d+)$/);

  if (suffixMatch) {
    // Если последняя часть соответствует паттерну К1, К2, Б1 и т.д.
    return `${projectCode}_${suffixMatch[1]}`;
  }

  // Если суффикса нет, возвращаем только код проекта
  return projectCode;
};

const getOvertimeStatus = (overtime_statuses) => {
  if (!overtime_statuses || Object.keys(overtime_statuses).length === 0) {
    return "pending";
  }

  const statuses = Object.values(overtime_statuses);

  // Если хотя бы один статус "Не согласован", считаем всю переработку не согласованной
  if (statuses.includes("Не согласован")) {
    return "rejected";
  }

  // Если все статусы "Согласован", считаем переработку согласованной
  if (statuses.every((status) => status === "Согласован")) {
    return "approved";
  }

  // В остальных случаях считаем, что переработка на рассмотрении
  return "pending";
};

const EditReport = () => {
  const { userData } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [projectBlocks, setProjectBlocks] = useState([]);
  const [availableWorkTypes, setAvailableWorkTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [hasAccessToViewAll, setHasAccessToViewAll] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const requestIdRef = useRef(0);

  const isWorkingDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // 0 - воскресенье, 6 - суббота
  };

  const getWorkingDaysAfterDate = (startDate, numberOfDays) => {
    let workingDays = 0;
    let currentDate = new Date(startDate);
    let resultDate = new Date(startDate);

    while (workingDays < numberOfDays) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (isWorkingDay(currentDate)) {
        workingDays++;
        resultDate = new Date(currentDate);
      }
    }
    return resultDate;
  };

  useEffect(() => {
    const checkAccess = async () => {
      const hasAccess = checkUserAccess(userData, "АДМС");
      setHasAccessToViewAll(hasAccess);
      // Устанавливаем текущего пользователя как выбранного
      setSelectedUser(userData);

      if (hasAccess) {
        try {
          setIsLoadingUsers(true);
          console.log("Начало загрузки пользователей");

          const usersData = await getUsersSummary({
            pageSize: 1000,
            isActive: true,
          });
          console.log("Полученные данные:", usersData);

          if (usersData && usersData.users && usersData.users.length > 0) {
            setUsers(usersData.users);
            console.log("Установленные пользователи:", usersData.users);
          } else {
            console.log("Нет данных пользователей в ответе");
          }
        } catch (error) {
          console.error("Ошибка при получении пользователей:", error);
          setError(error.message);
        } finally {
          setIsLoadingUsers(false);
        }
      }
    };

    if (userData) {
      checkAccess();
    }
  }, [userData]);

  useEffect(() => {
    const fetchReports = async () => {
      const myRequestId = ++requestIdRef.current;
      if (!userData) {
        console.log("Данные пользователя отсутствуют");
        if (requestIdRef.current === myRequestId) {
          setLoading(false);
        }
        return;
      }

      if (requestIdRef.current === myRequestId) {
        setIsLoadingReports(true);
      }

      try {
        const userId = selectedUser?.id || userData?.id;
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const isAdmin = checkUserAccess(userData, "АДМС");

        // Устанавливаем начальную дату для запроса
        let startDate;
        if (isAdmin) {
          if (currentMonth === 0) {
            // Январь
            startDate = new Date(currentYear - 1, 10, 1); // 1 ноября предыдущего года
          } else if (currentMonth === 1) {
            // Февраль
            startDate = new Date(currentYear - 1, 11, 1); // 1 декабря предыдущего года
          } else {
            startDate = new Date(currentYear, currentMonth - 2, 1); // 1-е число двух месяцев назад
          }
        } else {
          // Для обычных пользователей — загружаем с начала прошлого месяца (все отчёты отображаются, редактировать можно только за 4 дня)
          startDate = new Date(currentYear, currentMonth - 1, 1);
        }

        const factDateStart = startDate.toISOString().split("T")[0];

        // Получаем все страницы отчетов
        let allReports = [];
        let currentPage = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          if (requestIdRef.current !== myRequestId) {
            return;
          }
          const pageReports = await getAllReports(
            userId,
            factDateStart,
            currentPage,
            1000,
          );
          if (pageReports && pageReports.length > 0) {
            allReports = [...allReports, ...pageReports];
            currentPage++;
          } else {
            hasMorePages = false;
          }
        }

        // После загрузки всех страниц:
        const uniqueReportsMap = new Map();
        allReports.forEach((report) => {
          if (!uniqueReportsMap.has(report.id)) {
            uniqueReportsMap.set(report.id, report);
          }
        });
        const uniqueReports = Array.from(uniqueReportsMap.values());

        // Проверяем период редактирования
        const lastDayPrevMonth = new Date(currentYear, currentMonth, 0);
        const twoWorkingDaysAfter = getWorkingDaysAfterDate(
          lastDayPrevMonth,
          2,
        );
        const isWithinEditPeriod = today <= twoWorkingDaysAfter;

        // Фильтруем и форматируем отчеты
        const filteredAndFormattedReports = filterAndFormatReports(
          uniqueReports,
          currentMonth,
          currentYear,
          isWithinEditPeriod,
          userData,
        );

        if (requestIdRef.current === myRequestId) {
          setReports(filteredAndFormattedReports);
        }
      } catch (error) {
        console.error("Ошибка при загрузке отчетов:", error);
        if (requestIdRef.current === myRequestId) {
          setError(error.message);
        }
      } finally {
        if (requestIdRef.current === myRequestId) {
          setIsLoadingReports(false);
          setLoading(false);
        }
      }
    };

    fetchReports();
  }, [userData, selectedUser]);

  useEffect(() => {
    if (userData?.departments) {
      // Получаем все группы из всех департаментов
      const allGroups = userData.departments.flatMap(
        (dept) => dept.groups || [],
      );
      const allWorkTypes = allGroups.flatMap((group) =>
        group.roles.flatMap((role) => role.portal_job_type || []),
      );

      const uniqueWorkTypes = allWorkTypes.filter(
        (workType, index, self) =>
          index ===
          self.findIndex(
            (t) => t?.id === workType?.id && t?.name === workType?.name,
          ),
      );

      setAvailableWorkTypes(uniqueWorkTypes);
    }
  }, [userData]);

  const handleReportClick = (report) => {
    if (!report.isEditable) {
      return; // Прерываем выполнение если отчет нельзя редактировать
    }

    const formattedReport = {
      ...report,
      blocks: report.blocks.map((block) =>
        typeof block === "string" ? block : block.name,
      ),
    };
    setSelectedReport(formattedReport);

    if (report.project) {
      const selectedProject = userData.projects.find(
        (p) => p.id === report.project.id,
      );
      setProjectBlocks(selectedProject?.blocks || []);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
    setIsCreateMode(false);
    setError(null);
  };

  const handleSaveReport = async (
    updatedReport,
    isCreateMode,
    suppressPopup = false,
  ) => {
    try {
      let response;
      if (isCreateMode) {
        response = await sendReport(updatedReport);
      } else {
        try {
          response = await updateReport(updatedReport);
          if (!response) {
            throw new Error("Нет ответа от сервера");
          }
        } catch (updateError) {
          if (updateError.message.includes("Отчет уже согласован")) {
            if (!suppressPopup) {
              handleCloseModal(); // Закрываем модальное окно сразу
              setShowSuccessPopup(true);
              setError("Отчет уже согласован и не может быть изменен");
              setTimeout(() => {
                setShowSuccessPopup(false);
                setError(null);
              }, 3000);
            }
            return;
          }
          throw updateError;
        }
      }

      // Обновляем список отчетов только если не было ошибки и не подавляем попап
      if (!suppressPopup) {
        const userId = selectedUser?.id || userData?.id;
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        // Проверяем период редактирования
        const lastDayPrevMonth = new Date(currentYear, currentMonth, 0);
        const twoWorkingDaysAfter = getWorkingDaysAfterDate(
          lastDayPrevMonth,
          2,
        );
        const isWithinEditPeriod = today <= twoWorkingDaysAfter;

        const firstDayPrevMonth = new Date(currentYear, currentMonth - 1, 1);
        const factDateStart = firstDayPrevMonth.toISOString().split("T")[0];

        let allReports = [];
        let currentPage = 1;
        let hasMorePages = true;

        while (hasMorePages) {
          const pageReports = await getAllReports(
            userId,
            factDateStart,
            currentPage,
            1000,
          );
          if (pageReports && pageReports.length > 0) {
            allReports = [...allReports, ...pageReports];
            currentPage++;
          } else {
            hasMorePages = false;
          }
        }

        // Добавляем дедупликацию по id
        const uniqueReportsMap = new Map();
        allReports.forEach((report) => {
          if (!uniqueReportsMap.has(report.id)) {
            uniqueReportsMap.set(report.id, report);
          }
        });
        const uniqueReports = Array.from(uniqueReportsMap.values());

        // Фильтруем и форматируем отчеты
        const filteredAndFormattedReports = filterAndFormatReports(
          uniqueReports,
          currentMonth,
          currentYear,
          isWithinEditPeriod,
          userData,
        );

        // Обновляем состояние с отфильтрованными данными
        setReports(filteredAndFormattedReports);

        setError(null);
        handleCloseModal(); // Закрываем модальное окно сразу
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Ошибка при сохранении отчета:", error);
      if (!suppressPopup) {
        setShowSuccessPopup(true);
        setError("Произошла ошибка при сохранении отчета");
        setTimeout(() => {
          setShowSuccessPopup(false);
          setError(null);
        }, 3000);
      }
      throw error; // Пробрасываем ошибку дальше для обработки в модальном окне
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await deleteReport(reportId);

      // Обновляем список отчетов после удаления
      setReports((prevReports) =>
        prevReports.filter((report) => report.id !== reportId),
      );

      // Показываем попап успешного удаления
      handleCloseModal(); // Закрываем модальное окно сразу
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (error) {
      console.error("Ошибка при удалении отчета:", error);
      setError("Произошла ошибка при удалении отчета");
    }
  };

  const checkUserAccess = (user, accessLevel) => {
    return user?.access_levels?.some((level) => level.name === accessLevel);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "392px",
      opacity: state.isLoading || state.isDisabled ? 0.7 : 1,
      cursor: state.isLoading || state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: state.isLoading ? "#E0E0E0" : "#999",
      },
    }),
    // Остальные стили из AllReports...
  };

  const handleUserChange = async (selectedOption) => {
    try {
      setIsLoadingReports(true);
      // Получаем полную информацию о выбранном пользователе
      const fullUserData = await getUserById(selectedOption.value);
      if (!fullUserData || !fullUserData.id) {
        throw new Error("Данные пользователя не найдены");
      }

      setSelectedUser(fullUserData);

      // Дальше идет ваш существующий код для загрузки отчетов
      // ...
    } catch (error) {
      console.error("Ошибка при загрузке данных пользователя:", error);
      setError(error.message);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return (
      <div style={{ margin: "100px", fontSize: "20px" }}>
        Ошибка при загрузке отчетов: {error}
      </div>
    );
  }

  return (
    <div className="edit-report">
      <h2 className="edit-report__title">Доступные к редактированию отчеты</h2>
      <div className="edit-report-controls">
        {hasAccessToViewAll && (
          <div className="user-selector-container">
            <div className="user-selector">
              <Select
                isDisabled={isLoadingUsers || isLoadingReports}
                isLoading={isLoadingUsers}
                loadingMessage={() => "Загрузка списка сотрудников..."}
                components={{
                  LoadingIndicator,
                }}
                options={users.map((user) => ({
                  value: user.id,
                  label: `${user.surname} ${user.name}`,
                }))}
                value={
                  selectedUser
                    ? {
                        value: selectedUser.id,
                        label: `${selectedUser.surname} ${selectedUser.name}`,
                      }
                    : null
                }
                defaultValue={
                  userData
                    ? {
                        value: userData.id,
                        label: `${userData.surname} ${userData.name}`,
                      }
                    : null
                }
                onChange={handleUserChange}
                placeholder={
                  isLoadingUsers
                    ? "Загрузка пользователей..."
                    : users.length === 0
                      ? "Нет доступных пользователей"
                      : "Выберите сотрудника"
                }
                styles={customStyles}
                className="all-report_select"
                noOptionsMessage={() => "Нет доступных пользователей"}
              />
            </div>
            {selectedUser && (
              <button
                className="create-report-button"
                onClick={() => {
                  setSelectedReport(null);
                  setIsCreateMode(true);
                  setIsModalOpen(true);
                }}
              >
                Создать отчет
              </button>
            )}
          </div>
        )}
        <div className="overtime-status-legend">
          <div className="legend-title">Статус отчета:</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-color overtime-row_approved"></div>
              <span>Согласован</span>
            </div>
            <div className="legend-item">
              <div className="legend-color overtime-row_rejected"></div>
              <span>Не согласован</span>
            </div>
            <div className="legend-item">
              <div className="legend-color overtime-row_pending"></div>
              <span>На рассмотрении</span>
            </div>
            <div className="legend-item">
              <div className="legend-color dayoff-row_true"></div>
              <span>Отгул</span>
            </div>
            <div className="legend-item">
              <div className="legend-color remote-row_true"></div>
              <span>Удаленно</span>
            </div>
          </div>
        </div>
      </div>
      {isLoadingReports ? (
        <div className="loading">
          <Preloader />
          <p>Загрузка отчетов...</p>
        </div>
      ) : reports.length === 0 ? (
        <p style={{ fontSize: "20px" }}>
          Нет доступных отчетов за{" "}
          {new Date().toLocaleString("ru", { month: "long" })}
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Дата</th>
              <th>Проект</th>
              <th>Время</th>
              <th>Вид работ</th>
              <th>Комментарии</th>
              <th>Блоки</th>
              <th>Отдел</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr
                key={report.id}
                onClick={() => handleReportClick(report)}
                className={`
                  ${
                    selectedReport && selectedReport.id === report.id
                      ? "selected"
                      : ""
                  }
                  ${!report.isEditable ? "disabled-row" : ""}
                  ${
                    report.is_overtime
                      ? `overtime-row_${getOvertimeStatus(
                          report.overtime_statuses,
                        )}`
                      : ""
                  }
                  ${report.is_day_off ? "dayoff-row_true" : ""}
                  ${report.is_remote ? "remote-row_true" : ""}
                `}
              >
                <td>
                  {new Date(report.fact_date_start).toLocaleDateString(
                    "ru-RU",
                    {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    },
                  )}
                </td>
                <td>
                  {report.project
                    ? formatProjectName(report.project.name)
                    : "-"}
                </td>
                <td>
                  {report.time_spent
                    ? `${Math.floor(report.time_spent / 3600)}:${Math.floor(
                        (report.time_spent % 3600) / 60,
                      )
                        .toString()
                        .padStart(2, "0")}`
                    : "-"}
                </td>
                <td>{report.portal_job_type?.name || "-"}</td>
                <td>{report.comment || "-"}</td>
                <td>
                  {report.blocks && report.blocks.length > 0
                    ? Array.isArray(report.blocks)
                      ? report.blocks
                          .map((block) =>
                            typeof block === "string" ? block : block.name,
                          )
                          .join(", ")
                      : report.blocks
                    : "-"}
                </td>
                <td>{report.group?.name || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {isModalOpen && (isCreateMode || selectedReport) && (
        <EditReportModal
          report={selectedReport}
          onClose={handleCloseModal}
          onSave={handleSaveReport}
          onDelete={handleDeleteReport}
          userData={userData}
          projectBlocks={projectBlocks}
          setProjectBlocks={setProjectBlocks}
          selectedUser={selectedUser}
          isCreateMode={isCreateMode}
        />
      )}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className={`popup ${error ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${error ? "popup-icon-error" : ""}`}
            ></div>
            <p className="popup-text">
              {error || "Отчет успешно отредактирован"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditReport;
